import {
  Search,
  Handshake,
  Network,
  ArrowRight,
} from "lucide-react";
import { useVerificationDialog } from "@/contexts/VerificationDialogContext";

const services = [
  {
    icon: Search,
    pillar: "Pillar 01",
    title: "Supplier Sourcing & Matchmaking",
    description:
      "We locate the most capable and reliable Dominican manufacturers and suppliers to meet the exact demands of your US enterprise — pre-screened for capacity, quality standards, and commercial reliability.",
    bullets: [
      "Dominican supplier discovery & shortlisting",
      "Capability, capacity & quality screening",
      "Commercial reference verification",
    ],
    testId: "service-card-supplier-sourcing",
  },
  {
    icon: Handshake,
    pillar: "Pillar 02",
    title: "Cross-Border Negotiation",
    description:
      "We act as your local liaison on the ground in the Dominican Republic — overcoming cultural, language, and market barriers to negotiate the best terms, pricing, and production agreements on your behalf.",
    bullets: [
      "On-the-ground commercial negotiation",
      "Cultural & language bridging",
      "Pricing, MOQ & production terms",
    ],
    testId: "service-card-cross-border-negotiation",
  },
  {
    icon: Network,
    pillar: "Pillar 03",
    title: "Procurement Oversight & Liaison",
    description:
      "We manage the long-term B2B relationship between US buyers and DR suppliers — ensuring communications flow, milestones are tracked, and contracts are fulfilled without setbacks.",
    bullets: [
      "Ongoing trade-liaison support",
      "Milestone & communication tracking",
      "Contract performance oversight",
    ],
    testId: "service-card-procurement-oversight",
  },
];

export default function ServicesPillars() {
  const { openDialog } = useVerificationDialog();

  return (
    <section
      id="services"
      data-testid="services-section"
      className="bg-white pt-20 lg:pt-32 pb-12 lg:pb-16 border-t border-slate-200"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="max-w-3xl">
          <div className="eyebrow">Our Services</div>
          <h2 className="font-display mt-4 text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-slate-900">
            Three pillars of{" "}
            <span className="font-semibold">cross-border sourcing.</span>
          </h2>
          <p className="mt-5 text-lg text-slate-600 max-w-2xl leading-relaxed">
            PS GLOBAL SERIES, LLC is a B2B sourcing agency and trade liaison.
            We facilitate supplier matchmaking, cross-border negotiation, and
            procurement oversight between US buyers and Dominican Republic
            suppliers across all industries.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {services.map((s) => {
            const Icon = s.icon;
            return (
              <article
                key={s.title}
                data-testid={s.testId}
                className="group relative bg-white border border-slate-200 p-8 lg:p-10 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_40px_-12px_rgba(15,23,42,0.18)] hover:border-slate-300"
              >
                <div className="flex items-center justify-between">
                  <div className="h-11 w-11 inline-flex items-center justify-center bg-slate-900 text-white rounded-sm">
                    <Icon size={20} />
                  </div>
                  <div className="eyebrow text-[#B45309]">{s.pillar}</div>
                </div>

                <h3 className="font-display mt-7 text-2xl font-semibold tracking-tight text-slate-900">
                  {s.title}
                </h3>
                <p className="mt-3 text-[15px] text-slate-600 leading-relaxed">
                  {s.description}
                </p>

                <ul className="mt-6 space-y-2">
                  {s.bullets.map((b) => (
                    <li
                      key={b}
                      className="flex items-start gap-2 text-sm text-slate-700"
                    >
                      <span className="mt-2 h-px w-3 bg-[#B45309]" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>

                <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#B45309]/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              </article>
            );
          })}
        </div>

        <div className="mt-14 flex flex-col sm:flex-row items-start sm:items-center gap-5 sm:gap-8 border-t border-slate-200 pt-10">
          <div className="text-slate-700 text-[15px] max-w-xl">
            Ready to source?{" "}
            <span className="font-semibold text-slate-900">
              Tell us what you need sourced
            </span>{" "}
            and our trade-liaison team will respond within one business day.
          </div>
          <button
            type="button"
            onClick={openDialog}
            data-testid="services-cta-proposal"
            className="inline-flex items-center justify-center gap-2 bg-[#B45309] hover:bg-[#92400E] text-white px-6 py-3.5 text-sm font-semibold tracking-tight rounded-sm transition-all shadow-sm hover:shadow-md"
          >
            <Handshake size={16} />
            Tell Us What You Need Sourced
            <ArrowRight size={15} />
          </button>
        </div>
      </div>
    </section>
  );
}
